import { ReactNode } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Globe, FileText, Calendar, AlertTriangle, PenTool, BarChart3 } from "lucide-react"
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts"
import { Head, Link } from "@inertiajs/react"
import DashboardLayout from "@/layout/dashboard-layout"

interface HomeProps {
  blogs_count: number
  posts_count: number
  scheduled_posts_count: number
  alerts_count: number
  weekly_stats: Array<{
    name: string
    acessos: number
  }>
  recent_activities: Array<{
    type: string
    color: string
    title: string
    description: string
  }>
}

function Home({ blogs_count, posts_count, scheduled_posts_count, alerts_count, weekly_stats, recent_activities }: HomeProps) {
  const data = weekly_stats 

  return (
    <DashboardLayout>
      <Head title="Dashboard" />
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Dashboard</h1>
          <p className="text-muted-foreground">Visão geral da sua rede de blogs Memoráveis</p>
        </div>

        {/* Cards de Estatísticas */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Blogs Ativos</CardTitle>
              <Globe className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{blogs_count}</div>
              <p className="text-xs text-muted-foreground">+2 desde o mês passado</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Postagens (7 dias)</CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{posts_count}</div>
              <p className="text-xs text-muted-foreground">+12% em relação à semana anterior</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Agendamentos</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{scheduled_posts_count}</div>
              <p className="text-xs text-muted-foreground">Para os próximos 7 dias</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Alertas</CardTitle>
              <AlertTriangle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{alerts_count}</div>
              <p className="text-xs text-muted-foreground">
                <Badge variant="destructive" className="text-xs">
                  Crítico
                </Badge>
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Gráfico de Acessos */}
        <Card>
          <CardHeader>
            <CardTitle>Acessos nos Últimos 7 Dias</CardTitle>
            <CardDescription>Dados integrados do Google Analytics</CardDescription>
          </CardHeader>
          <CardContent className="pl-2">
            <div className="h-[350px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="acessos" stroke="#2563eb" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Atalhos Rápidos */}
        <Card>
          <CardHeader>
            <CardTitle>Atalhos Rápidos</CardTitle>
            <CardDescription>Acesse rapidamente as funcionalidades mais utilizadas</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3">
              <Button className="h-20 flex-col gap-2 bg-transparent" variant="outline" asChild>
                <Link href="/dashboard/posts/new">
                  <PenTool className="h-6 w-6" />
                  Nova Postagem em Massa
                </Link>
              </Button>
              <Button className="h-20 flex-col gap-2 bg-transparent" variant="outline" asChild>
                <Link href="/dashboard/blogs">
                  <Globe className="h-6 w-6" />
                  Gerenciar Blogs
                </Link>
              </Button>
              <Button className="h-20 flex-col gap-2 bg-transparent" variant="outline" asChild>
                <Link href="/dashboard/posts">
                  <BarChart3 className="h-6 w-6" />
                  Estatísticas Detalhadas
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Atividade Recente */}
        <Card>
          <CardHeader>
            <CardTitle>Atividade Recente</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recent_activities.map((activity, index) => (
                <div key={index} className="flex items-center space-x-4">
                  <div className={`w-2 h-2 ${activity.color} rounded-full`}></div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">{activity.title}</p>
                    <p className="text-xs text-muted-foreground">{activity.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  )
}

Home.layout = (page: ReactNode) => (
  <DashboardLayout>{page}</DashboardLayout>
)

export default Home
